/**
 * useWeeklyInsight
 *
 * Drives the weekly insight trigger logic and returns render-ready state.
 *
 * On every Home mount:
 * 1. Determine current weekOf (Monday of this week).
 * 2. Call GET /latest to check if an insight already exists for this weekOf.
 * 3. If none exists: build payload, call POST /compute.
 * 4. Return { insight, isLoading, error }.
 *
 * Non-critical: if either API call fails, error is set and insight is null.
 * The Home screen must not crash if this hook errors.
 */

import { useEffect, useState } from 'react';
import { useAppContext } from '../store/AppContext';
import {
  getMondayOf,
  buildWeeklyPayload,
  computeWeeklyInsight,
  fetchLatestWeeklyInsight,
  hasComputedToday,
  markComputedToday,
} from '../services/weeklyInsight.service';
import type { WeeklyInsightResponse } from '../services/weeklyInsight.service';

interface UseWeeklyInsightResult {
  insight: WeeklyInsightResponse | null;
  isLoading: boolean;
  error: string | null;
}

export function useWeeklyInsight(): UseWeeklyInsightResult {
  const { user, isDemoMode, isPremium, glp1Enabled, dailySummary } = useAppContext();
  const { targets, tdee } = dailySummary;

  const [insight, setInsight] = useState<WeeklyInsightResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDemoMode || !user?.uid) return;
    if (!targets) return;

    const userId = user.uid;
    const weekOf = getMondayOf(new Date());
    const resolvedTargets = targets;

    let cancelled = false;

    async function run() {
      setIsLoading(true);
      setError(null);

      try {
        // If already computed today for this weekOf, just fetch and render
        if (hasComputedToday(weekOf)) {
          const existing = await fetchLatestWeeklyInsight();
          if (!cancelled) {
            setInsight(existing);
            setIsLoading(false);
          }
          return;
        }

        // Not computed today — build payload and compute
        const userTdee = tdee?.baseTdee ?? 0;
        const payload = await buildWeeklyPayload(
          userId,
          weekOf,
          resolvedTargets,
          isPremium,
          glp1Enabled,
          userTdee,
        );

        const result = await computeWeeklyInsight(payload);
        markComputedToday(weekOf);

        if (!cancelled) {
          setInsight(result);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[useWeeklyInsight] Failed:', err);
          setError('weekly_insight_unavailable');
          setIsLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, isDemoMode, isPremium, glp1Enabled, targets, tdee]);

  return { insight, isLoading, error };
}
